import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Cookie, Shield } from 'lucide-react';

export const CookiePolicy: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const menuItems = [
        { label: 'Features', href: '/#features' },
        { label: 'Blog', href: '/blog' },
        { label: 'Press', href: '/press' },
        { label: 'Pitch', href: '/pitch' },
        { label: 'Enterprise API', href: '/enterprise-api' }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>Cookie Policy – How Cura Uses Cookies</title>
                <meta name="description" content="Understand how Cura uses cookies and similar technologies. Learn about cookie types, your control options, and our commitment to your privacy ✓" />
                <link rel="canonical" href="https://mycura.org/cookie-policy" />
                <meta property="og:title" content="Cookie Policy – How Cura Uses Cookies" />
                <meta property="og:description" content="Understand how Cura uses cookies and similar technologies. Learn about cookie types, your control options, and our commitment to your privacy." />
                <meta property="og:url" content="https://mycura.org/cookie-policy" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Cookie Policy – How Cura Uses Cookies" />
                <meta name="twitter:description" content="Understand how Cura uses cookies and similar technologies. Learn about cookie types, your control options, and our commitment to your privacy." />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Cura Cookie Policy",
                        "url": "https://mycura.org/cookie-policy",
                        "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
                    })}
                </script>
            </Helmet>
            <Navigation
                menuItems={menuItems}
                scrollToSection={(id) => {
                    const element = document.getElementById(id);
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                scrollToTop={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />

            <main className="pt-32 pb-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-16"
                    >
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 text-[#003b82] mb-6">
                            <Cookie className="w-8 h-8" />
                        </div>
                        <h1 className="text-4xl md:text-5xl text-slate-900 mb-6">
                            Cookie Policy
                        </h1>
                        <p className="text-xl text-slate-500 max-w-2xl mx-auto">
                            How we use cookies and similar technologies to improve your experience.
                        </p>
                    </motion.div>

                    <div className="prose prose-lg prose-slate max-w-none">
                        <p className="lead">
                            This Cookie Policy explains how Cura ("we", "us", and "our") uses cookies and similar technologies to recognize you when you visit our website at mycura.org. It explains what these technologies are and why we use them, as well as your rights to control our use of them.
                        </p>

                        <h3>What are cookies?</h3>
                        <p>
                            Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.
                        </p>

                        <h3>Why do we use cookies?</h3>
                        <p>
                            We use first-party and third-party cookies for several reasons. Some cookies are required for technical reasons in order for our Website to operate, and we refer to these as "essential" or "strictly necessary" cookies. Other cookies also enable us to track and target the interests of our users to enhance the experience on our Online Properties. Third parties serve cookies through our Website for advertising, analytics and other purposes.
                        </p>

                        <div className="bg-slate-50 p-8 rounded-2xl my-8 border border-slate-100">
                            <h4 className="flex items-center gap-2 text-[#003b82] mt-0">
                                <Shield className="w-5 h-5" />
                                Your Privacy Matters
                            </h4>
                            <p className="mb-0 text-sm">
                                We respect your privacy and are committed to protecting your personal data. You can choose to disable cookies through your browser settings, though this may affect your ability to use certain features of our website.
                            </p>
                        </div>

                        <h3>Types of Cookies We Use</h3>
                        <ul>
                            <li>
                                <strong>Essential Cookies:</strong> These are strictly necessary to provide you with services available through our Website and to use some of its features, such as access to secure areas.
                            </li>
                            <li>
                                <strong>Performance and Functionality Cookies:</strong> These are used to enhance the performance and functionality of our Website but are non-essential to their use. However, without these cookies, certain functionality (like videos) may become unavailable.
                            </li>
                            <li>
                                <strong>Analytics and Customization Cookies:</strong> These collect information that is used either in aggregate form to help us understand how our Website is being used or how effective our marketing campaigns are, or to help us customize our Website for you.
                            </li>
                        </ul>

                        <h3>How can I control cookies?</h3>
                        <p>
                            You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the Cookie Consent Manager. The Cookie Consent Manager allows you to select which categories of cookies you accept or reject. Essential cookies cannot be rejected as they are strictly necessary to provide you with services.
                        </p>

                        <h3>Updates to this Policy</h3>
                        <p>
                            We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.
                        </p>

                        <div className="mt-12 pt-8 border-t border-slate-200">
                            <p className="text-sm text-slate-500">
                                Last updated: November 25, 2024
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default CookiePolicy;
